import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { DrawerContext, ModalModel } from '@notino/react-styleguide';

import { ADD_TO_CART_MODAL_TITLE_ID } from '@sharedComponents/AddToCartButton/AddToCartModal/constants';
import { StyledHeader } from '@sharedComponents/AddToCartButton/AddToCartModal/styled';
import messages from '@sharedComponents/AddToCartButton/messages';
import { IAddToCartButtonProps } from '@sharedComponents/AddToCartButton/model';
import { NotificationModalOrDrawerContent } from '@sharedComponents/AddToCartButton/NotificationModal';

import { MaxProductsInCartModal } from './MaxProductsInCartModal';

// Lazy-loaded to break the static circular dependency:
// BuyButton → AddToCartButton → getModalAfterAddToCart → AddToCartModal → UpsellingList → BuyButton
const AddToCartModal = React.lazy(() =>
  import('@sharedComponents/AddToCartButton/AddToCartModal').then((m) => ({
    default: m.AddToCartModal,
  }))
);

export const getModalAfterAddToCart: IAddToCartButtonProps['getModalAfterAddToCart'] =
  (props) => {
    if (props.type === 'common' && props.notification?.modalType === 'common') {
      return [
        <NotificationModalOrDrawerContent
          key="notification"
          statusMessages={props.notification.messages}
          messagesValues={props.notification.values}
        />,
        {
          center: false,
          withFocusTrap: true,
          header: (
            <StyledHeader>
              <FormattedMessage {...messages[props.notification.header]} />
            </StyledHeader>
          ),
        },
        {
          header: (
            <StyledHeader>
              <FormattedMessage {...messages[props.notification.header]} />
            </StyledHeader>
          ),
        },
      ];
    } else if (props.type === 'upselling') {
      // also enables the bottom sheet's focus trap, which is off without it
      const drawerInitialFocusRef = React.createRef<HTMLDivElement>();

      return [
        <DrawerContext.Provider key="upselling">
          <React.Suspense
            fallback={<button type="button" style={{ opacity: 0 }} />} // invisible button to make FocusTrap happy
          >
            <AddToCartModal
              products={props.products}
              response={props.response}
              closingModal={props.onClosingModal}
              returnFocus={props.returnFocus}
              services={props.services}
            />
          </React.Suspense>
        </DrawerContext.Provider>,
        {
          center: false,
          onClose: props.onClose,
          size: ModalModel.ModalSizes.large,
          withFocusTrap: true,
          noBorders: true,
          disableCloseButton: true,
          'aria-labelledby': ADD_TO_CART_MODAL_TITLE_ID,
        },
        {
          noHeaderHandle: true,
          'aria-labelledby': ADD_TO_CART_MODAL_TITLE_ID,
          initialFocusRef: drawerInitialFocusRef,
          onDismiss: props.returnFocus,
          header: (
            <StyledHeader ref={drawerInitialFocusRef} tabIndex={-1}>
              <FormattedMessage {...messages.addedToCart} />
            </StyledHeader>
          ),
        },
      ];
    } else if (props.type === 'maxProductsInCart') {
      return [
        <MaxProductsInCartModal key="maxProductsInCart" />,
        {
          withFocusTrap: true,
        },
        {},
      ];
    }
    return undefined!;
  };
